import React, { useCallback, useEffect, useRef, useState } from 'react';

import styled from '@emotion/styled';

import { FontWeight } from '@clutter/clean';

import { useTrack } from '@root/initializers/wt';

import learnMore1 from '@images/checkout/challenger/learn_more_1.png';
import learnMore2 from '@images/checkout/challenger/learn_more_2.png';
import learnMore3 from '@images/checkout/challenger/learn_more_3.png';
import learnMore4 from '@images/checkout/challenger/learn_more_4.png';

import { useModalFocus } from './hooks/use_modal_focus';
import { MOBILE } from './media';
import { CHALLENGER as T } from './tokens';

type Slide = {
  title: string;
  body: string;
  image: string;
};

/** Same cadence as home security carousel progress. */
const CYCLE_MS = 4000;
const IDLE_RESUME_MS = 5000;

/** Figma Learn more 1–4 (707:13352, 707:13007, 707:13697, 707:14042). */
const SLIDES: Slide[] = [
  {
    title: 'Trusted with the world’s most valuable assets',
    body: 'Our secure facilities are purpose-built for long-term protection. Access is restricted to trained personnel and backed by 24/7 video surveillance.',
    image: learnMore1,
  },
  {
    title: 'Your items are always within reach',
    body: 'Every item is photographed, catalogued, and uploaded to your account portal before leaving your home. When you need an item returned, simply request it directly from your account.',
    image: learnMore2,
  },
  {
    title: 'Full-service pickup and packing',
    body: 'Save the trip to the warehouse. Our professionals handle the packing, wrapping, and loading for you. Every plan includes pickup and return, with complimentary disassembly available.',
    image: learnMore3,
  },
  {
    title: 'No surprises on your bill',
    body: 'Your rate is calculated based on the exact space you occupy. If plans change, cancel at no charge up to 2 weeks before your initial pickup.',
    image: learnMore4,
  },
];

/**
 * Figma 707:14041 — overlay/overlay-medium + 4px blur behind the dialog.
 */
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${T.space[16]};
  box-sizing: border-box;
  background: rgba(4, 7, 17, 0.4);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
`;

/**
 * Desktop 707:13352 — 420×536, gap 12.
 * Mobile 707:18522 — 370×496, gap 8.
 */
const Dialog = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${T.space[12]};
  width: 100%;
  max-width: 420px;
  height: 536px;
  max-height: min(536px, calc(100vh - 32px));
  max-height: min(536px, calc(100dvh - 32px));
  padding: ${T.space[16]};
  box-sizing: border-box;
  border-radius: 16px;
  background: ${T.color.bgPage};
  font-family: ${T.font.sans};
  color: ${T.color.textPrimary};

  ${MOBILE} {
    max-width: 370px;
    height: 496px;
    max-height: min(496px, calc(100vh - 32px));
    max-height: min(496px, calc(100dvh - 32px));
    gap: ${T.space[8]};
  }
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1;
  min-height: 0;
  gap: ${T.space[24]};
  width: 100%;

  ${MOBILE} {
    gap: ${T.space[16]};
  }
`;

const MediaBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${T.space[8]};
  width: 100%;
`;

/** Figma Features containers (707:13355 / 13010 / 13700 / 14045) — 240px cover. */
const Media = styled.img`
  display: block;
  width: 100%;
  height: 240px;
  object-fit: cover;
  border-radius: ${T.radius.md};
  background: ${T.color.summarySurface};
  flex-shrink: 0;
`;

const Pagination = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${T.space[8]};
  width: 100%;
  padding: ${T.space[12]} ${T.space[16]};
  box-sizing: border-box;
`;

/** Inactive = 8px dot; active = 64px track with filling progress (home security). */
const Dot = styled.button`
  appearance: none;
  border: none;
  padding: 0;
  display: block;
  height: 8px;
  width: 8px;
  border-radius: ${T.radius.pill};
  background: rgba(154, 161, 165, 0.4);
  flex-shrink: 0;
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }
`;

const ActiveTrack = styled.button`
  appearance: none;
  border: none;
  padding: 0;
  position: relative;
  display: block;
  height: 8px;
  width: 64px;
  border-radius: ${T.radius.pill};
  background: rgba(154, 161, 165, 0.4);
  flex-shrink: 0;
  overflow: hidden;
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }
`;

const ActiveFill = styled.span<{ $paused?: boolean }>`
  position: absolute;
  inset: 0 auto 0 0;
  height: 8px;
  width: 100%;
  border-radius: inherit;
  background: ${T.color.textSecondary};
  animation: learn-more-progress ${CYCLE_MS}ms linear forwards;

  ${({ $paused }) => $paused && 'animation-play-state: paused;'}

  @keyframes learn-more-progress {
    from {
      transform: translateX(-100%);
    }
    to {
      transform: translateX(0);
    }
  }
`;

/**
 * Desktop 707:13352 — copy frame 146 (title up to 46 + gap 12 + body 88).
 * Mobile 707:18522 — copy frame 106 (title 22 + gap 12 + body 72).
 */
const Copy = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  width: 100%;
  height: 146px;
  overflow: hidden;

  ${MOBILE} {
    height: 106px;
  }
`;

const Title = styled.h2`
  margin: 0;
  width: 100%;
  flex-shrink: 0;
  font-family: ${T.font.sans};
  font-size: 20px;
  font-weight: ${FontWeight.Medium};
  line-height: 23px;
  color: ${T.color.textPrimary};

  ${MOBILE} {
    font-size: 16px;
    line-height: 22px;
  }
`;

const Description = styled.p`
  margin: 0;
  width: 100%;
  height: 88px;
  overflow: hidden;
  font-family: ${T.font.sans};
  font-size: 14px;
  font-weight: ${FontWeight.Regular};
  line-height: 20px;
  color: ${T.color.textPrimary};

  ${MOBILE} {
    height: 72px;
    font-size: 13px;
    line-height: 18px;
  }
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  flex-shrink: 0;
`;

const FooterRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  ${MOBILE} {
    gap: ${T.space[8]};
  }
`;

/**
 * Figma 707:13007 Back = outlined; Close = ghost; last slide Back = ghost
 * (707:14042). Hidden slots keep layout via visibility.
 * Mobile buttons: 12/14 (707:18522 / 707:18818).
 */
const GhostButton = styled.button<{ $hidden?: boolean; $outlined?: boolean }>`
  appearance: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${T.space[12]} ${T.space[16]};
  border: ${({ $outlined }) =>
    $outlined ? `1px solid ${T.color.borderDefault}` : '1px solid transparent'};
  border-radius: ${T.radius.sm};
  background: transparent;
  cursor: pointer;
  font-family: ${T.font.sans};
  font-size: 14px;
  font-weight: ${FontWeight.Semibold};
  line-height: 18px;
  color: ${T.color.textPrimary};
  white-space: nowrap;
  visibility: ${({ $hidden }) => ($hidden ? 'hidden' : 'visible')};
  pointer-events: ${({ $hidden }) => ($hidden ? 'none' : 'auto')};

  &:hover {
    opacity: 0.85;
  }

  ${MOBILE} {
    font-size: 12px;
    line-height: 14px;
  }
`;

const PrimaryButton = styled.button`
  appearance: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${T.space[12]} ${T.space[16]};
  border: none;
  border-radius: ${T.radius.sm};
  background: ${T.color.textBrand};
  cursor: pointer;
  font-family: ${T.font.sans};
  font-size: 14px;
  font-weight: ${FontWeight.Semibold};
  line-height: 18px;
  color: ${T.color.textInverse};
  white-space: nowrap;

  &:hover {
    opacity: 0.9;
  }

  ${MOBILE} {
    font-size: 12px;
    line-height: 14px;
  }
`;

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

export const LearnMoreModal: React.FC<Props> = ({ isOpen, onClose }) => {
  const track = useTrack({
    action: 'click',
    container: 'features_overlay',
  });
  const dialogRef = useRef<HTMLDivElement>(null);
  useModalFocus(isOpen, dialogRef);
  const [index, setIndex] = useState(0);
  const [progressKey, setProgressKey] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const isPausedRef = useRef(false);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cycleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const last = index === SLIDES.length - 1;
  const slide = SLIDES[index];

  const clearIdleTimer = useCallback(() => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
  }, []);

  const clearCycleTimer = useCallback(() => {
    if (cycleTimerRef.current) {
      clearTimeout(cycleTimerRef.current);
      cycleTimerRef.current = null;
    }
  }, []);

  const bumpProgress = useCallback(() => {
    setProgressKey((key) => key + 1);
  }, []);

  const goTo = useCallback(
    (nextIndex: number) => {
      if (nextIndex !== index) {
        track({
          objectName: nextIndex > index ? 'next' : 'prev',
          position: index,
        });
      }
      setIndex(nextIndex);
      clearIdleTimer();
      setIsPaused(true);
      isPausedRef.current = true;
      bumpProgress();
      idleTimerRef.current = setTimeout(() => {
        setIsPaused(false);
        isPausedRef.current = false;
        bumpProgress();
      }, IDLE_RESUME_MS);
    },
    [bumpProgress, clearIdleTimer, index, track],
  );

  useEffect(() => {
    isPausedRef.current = isPaused;
  }, [isPaused]);

  useEffect(() => {
    if (!isOpen) {
      clearIdleTimer();
      clearCycleTimer();
      setIndex(0);
      setIsPaused(false);
      isPausedRef.current = false;
      setProgressKey(0);
      return;
    }

    setIndex(0);
    setIsPaused(false);
    isPausedRef.current = false;
    bumpProgress();
  }, [bumpProgress, clearCycleTimer, clearIdleTimer, isOpen]);

  useEffect(() => {
    clearCycleTimer();
    if (!isOpen || isPaused) return clearCycleTimer;

    cycleTimerRef.current = setTimeout(() => {
      if (isPausedRef.current) return;
      setIndex((value) => (value + 1) % SLIDES.length);
      bumpProgress();
    }, CYCLE_MS);

    return clearCycleTimer;
  }, [bumpProgress, clearCycleTimer, index, isOpen, isPaused, progressKey]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  useEffect(
    () => () => {
      clearIdleTimer();
      clearCycleTimer();
    },
    [clearCycleTimer, clearIdleTimer],
  );

  if (!isOpen) return null;

  const onOverlayClick: React.MouseEventHandler<HTMLDivElement> = (event) => {
    if (event.currentTarget === event.target) onClose();
  };

  return (
    <Overlay onClick={onOverlayClick} role="presentation">
      <Dialog
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="challenger-learn-more-title"
        onClick={(event) => event.stopPropagation()}
      >
        <Body>
          <MediaBlock>
            <Media src={slide.image} alt="" aria-hidden />
            <Pagination role="group" aria-label="Slide progress">
              {SLIDES.map((item, i) =>
                i === index ? (
                  <ActiveTrack
                    key={`active-${i}`}
                    type="button"
                    aria-label={`${item.title}, playing`}
                    aria-current="step"
                    onClick={() => goTo(i)}
                  >
                    <ActiveFill
                      key={progressKey}
                      $paused={isPaused}
                      style={{ animationDuration: `${CYCLE_MS}ms` }}
                    />
                  </ActiveTrack>
                ) : (
                  <Dot
                    key={`dot-${i}`}
                    type="button"
                    aria-label={`Go to ${item.title}`}
                    onClick={() => goTo(i)}
                  />
                ),
              )}
            </Pagination>
          </MediaBlock>
          <Copy>
            <Title id="challenger-learn-more-title">{slide.title}</Title>
            <Description>{slide.body}</Description>
          </Copy>
        </Body>
        <Footer>
          <GhostButton
            type="button"
            $hidden={index === 0}
            $outlined={index > 0 && !last}
            onClick={() => goTo(Math.max(0, index - 1))}
          >
            Back
          </GhostButton>
          <FooterRight>
            <GhostButton type="button" $hidden={last} onClick={onClose}>
              Close
            </GhostButton>
            {last ? (
              <PrimaryButton type="button" onClick={onClose}>
                Done
              </PrimaryButton>
            ) : (
              <PrimaryButton
                type="button"
                onClick={() => goTo(Math.min(SLIDES.length - 1, index + 1))}
              >
                Next
              </PrimaryButton>
            )}
          </FooterRight>
        </Footer>
      </Dialog>
    </Overlay>
  );
};
